export const RELATED_CATEGORY_EVENT_GROUP = 'Relevant Category';
export const RELATED_CATEGORY_EVENTS = {
	view: 'Find a CTA View',
	click: 'Find a CTA Click',
	skip: 'Find a CTA Skip',
};
export const RELATED_CATEGORY_BARK_MODE = 'related-category-selection';

/**
 * Maps the related-categories api response to the data the card needs.
 * Eligibility is decided server-side: the api returns no category for
 * ineligible projects, in which case this returns null and no card renders.
 * variant is kept null when absent so it can't be confused with control (0).
 */
export const getRelatedCategoryCardData = (apiResponse) => {
	const category = apiResponse && apiResponse.category;
	if (!category) {
		return null;
	}
	const location = apiResponse.location;
	return {
		categoryId: category.id,
		categoryName: category.name,
		categorySlug: category.slug,
		location:
			location && location.id
				? { id: location.id, name: location.name, type: location.type }
				: null,
		variant: apiResponse.variant ?? null,
	};
};

/**
 * Renders the related-category card from the #related-category-card-template
 * Handlebars template embedded in seller-list.twig. All values are escaped by
 * Handlebars, so label strings interpolating api data are safe to pass raw.
 *
 * @param {Object} cardData
 * @param {number} cardData.categoryId
 * @param {string} cardData.eyebrow
 * @param {string} cardData.timeEstimate
 * @param {string} cardData.title
 * @param {string} cardData.subtitle
 * @param {string} cardData.ctaLabel
 * @param {string} cardData.skipLabel
 * @returns {string} html, or '' when the page has no template
 */
export const buildRelatedCategoryCardHtml = (cardData) => {
	const source = document.getElementById('related-category-card-template');
	if (!source || !window.Handlebars) {
		return '';
	}
	return window.Handlebars.compile(source.innerHTML)(cardData);
};

export const buildRelatedCategoryTrackingParams = ({
	projectId,
	projectHashId,
	cardData,
}) => ({
	...(projectId ? { existing_project_id: Number(projectId) } : {}),
	existing_project_hash_id: projectHashId,
	new_category: cardData.categoryName,
	new_category_id: cardData.categoryId,
	variant: cardData.variant,
});

/**
 * Whether to (re)insert the card and whether that insertion counts as a view.
 * The list container is emptied and rebuilt on every page, sort and filter
 * change, so the card has to be re-inserted — but only ever viewed once.
 *
 * @param {Object} args
 * @param {boolean} args.dismissed buyer skipped the card earlier in this visit
 * @param {boolean} args.viewTracked view already sent for this page load
 * @param {number|string} args.currentPage matches page on show, unknown === 1
 * @param {number} args.reactCardsVariant non-zero means React owns the list
 * @returns {{render: boolean, trackView: boolean}}
 */
export const planRelatedCategoryRender = ({
	dismissed,
	viewTracked,
	currentPage,
	reactCardsVariant,
}) => {
	const render =
		!dismissed && (Number(currentPage) || 1) === 1 && (reactCardsVariant ?? 0) === 0;
	return { render, trackView: render && !viewTracked };
};

export const buildRelatedCategoryNrfParams = ({ cardData, existingProjectHashId }) => {
	const params = new URLSearchParams({
		nrfp: 'true',
		bark_mode: RELATED_CATEGORY_BARK_MODE,
	});

	if (existingProjectHashId) {
		params.set('existing_project_hash_id', existingProjectHashId);
	}

	if (cardData.location) {
		params.set('locid', cardData.location.id);
		params.set('pt', cardData.location.type);
		params.set('locname', cardData.location.name ?? '');
		params.set('prefilled_loc', 'true');
	}

	return params;
};
